import React from 'react';
import FormSection from './FormSection';
import { TextArea } from '../common';
import Select from '../common/Select';
import ChecklistLookup from '../common/ChecklistLookup';
import { 
  SCHEDULE_STATUS_OPTIONS,
  YES_NO_OPTIONS
} from './constants';

const StatusInfo = ({ formData, onChange, errors = {} }) => {
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear error for the field if it has a value
    const updatedData = { [name]: value };
    const updatedErrors = { ...errors };
    
    // Special handling for scheduleStatus field
    if (name === 'scheduleStatus') {
      // If changing to Deferred, set isDeferred to Y
      if (value === 'Deferred') {
        updatedData.isDeferred = 'Y';
      }
      // If changing from Deferred to something else, set isDeferred to N and clear reasonForDeferral
      else if (formData.scheduleStatus === 'Deferred') {
        updatedData.isDeferred = 'N';
        updatedData.reasonForDeferral = '';
      }
    }
    
    // Special handling for isDeferred field
    if (name === 'isDeferred') {
      if (value === 'Y') {
        // If changing to Yes, set scheduleStatus to Deferred
        updatedData.scheduleStatus = 'Deferred';
      } else {
        // If changing to No, clear reasonForDeferral and set scheduleStatus to Not Scheduled
        updatedData.reasonForDeferral = '';
        updatedData.scheduleStatus = 'Not Scheduled';
      }
    }
    
    if (value) {
      delete updatedErrors[name];
      onChange(updatedData, updatedErrors);
    } else {
      onChange(updatedData);
    }
  };

  const handleChecklistSelect = (checklist) => {
    const updatedData = {
      checklist: checklist.id,
      checklistDetails: {
        revisionNumber: checklist.revision_number,
        title: checklist.title,
        description: checklist.description_short,
        pdfLink: checklist.pdf_document_link
      }
    };

    // Clear error when checklist is selected
    const updatedErrors = { ...errors };
    delete updatedErrors.checklist;
    
    onChange(updatedData, updatedErrors);
  };

  return (
    <FormSection title="Status & Additional Info">
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Select
            label="Schedule Status"
            name="scheduleStatus"
            value={formData.scheduleStatus}
            onChange={handleInputChange}
            options={SCHEDULE_STATUS_OPTIONS}
            error={errors.scheduleStatus}
            required
          />

          <Select
            label="Is Deferred"
            name="isDeferred"
            value={formData.isDeferred}
            onChange={handleInputChange}
            options={YES_NO_OPTIONS}
            error={errors.isDeferred}
            disabled={formData.scheduleStatus !== 'Deferred'} // Only enable if status is Deferred
          />

          <Select
            label="Is Audit Report Generated"
            name="isAuditReportGenerated"
            value={formData.isAuditReportGenerated}
            onChange={handleInputChange}
            options={YES_NO_OPTIONS}
            error={errors.isAuditReportGenerated}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Is Audit Schedule Created"
            name="isAuditScheduleCreated"
            value={formData.isAuditScheduleCreated}
            onChange={handleInputChange}
            options={YES_NO_OPTIONS}
            error={errors.isAuditScheduleCreated}
          />

          <ChecklistLookup
            value={formData.checklist}
            onChange={(checklistId) => {
              const updatedData = { checklist: checklistId };
              const updatedErrors = { ...errors };
              if (checklistId) {
                delete updatedErrors.checklist;
                onChange(updatedData, updatedErrors);
              } else {
                onChange(updatedData);
              }
            }}
            onChecklistSelect={handleChecklistSelect}
            error={errors.checklist}
          />
        </div>

        {formData.checklistDetails && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Checklist</h4>
            <div className="space-y-2 text-sm text-gray-600">
              <div>
                <span className="font-medium">Revision:</span> {formData.checklistDetails.revisionNumber}
              </div>
              <div>
                <span className="font-medium">Title:</span> {formData.checklistDetails.title}
              </div>
              <div>
                <span className="font-medium">Description:</span> {formData.checklistDetails.description}
              </div>
              {formData.checklistDetails.pdfLink && (
                <div>
                  <a
                    href={formData.checklistDetails.pdfLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    View Checklist PDF
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        <TextArea
          label="Remarks"
          name="remarks"
          value={formData.remarks}
          onChange={handleInputChange}
          error={errors.remarks}
          rows={4}
          placeholder="Enter any additional remarks or notes"
        />

        {formData.scheduleStatus === 'Deferred' && (
          <TextArea
            label="Reason for Deferral"
            name="reasonForDeferral"
            value={formData.reasonForDeferral}
            onChange={handleInputChange}
            error={errors.reasonForDeferral}
            rows={2}
            required
          />
        )}

        {/* Read-only fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <TextArea
            label="Audit Report Number"
            name="auditReportNumber"
            value={formData.auditReportNumber}
            disabled
            rows={1}
            className="bg-gray-50"
          />

          <TextArea
            label="Previous Audit Report Link"
            name="previousAuditReportLink"
            value={formData.previousAuditReportLink}
            disabled
            rows={1}
            className="bg-gray-50"
          />
        </div>
      </div>
    </FormSection>
  );
};

export default StatusInfo;
